import React, { useState } from "react";
import { Smartphone, Tablet, Laptop, Monitor, Type, Bold, AlignLeft } from "lucide-react";

// Unified responsive styles for a single class
export interface UnifiedResponsiveStyles {
  xs: BreakpointStyle;
  sm: BreakpointStyle;
  md: BreakpointStyle;
  lg: BreakpointStyle;
  xl: BreakpointStyle;
}

export interface BreakpointStyle {
  fontSize: string;
  fontWeight: string;
  textAlign: string;
}

interface ResponsiveStylesModalProps {
  isOpen: boolean;
  responsiveStyles: UnifiedResponsiveStyles;
  onStylesChange: (styles: UnifiedResponsiveStyles) => void;
  onApply: () => void;
  onClose: () => void;
}

const fontSizeOptions = [
  { value: "", label: "None" },
  { value: "0", label: "0px" },
  { value: "12", label: "12px" },
  { value: "14", label: "14px" },
  { value: "16", label: "16px" },
  { value: "18", label: "18px" },
  { value: "20", label: "20px" },
  { value: "24", label: "24px" },
  { value: "28", label: "28px" },
  { value: "32", label: "32px" },
  { value: "36", label: "36px" },
  { value: "40", label: "40px" },
  { value: "48", label: "48px" },
  { value: "56", label: "56px" },
  { value: "64", label: "64px" },
];

const fontWeightOptions = [
  { value: "", label: "None" },
  { value: "100", label: "Thin (100)" },
  { value: "200", label: "Extra Light (200)" },
  { value: "300", label: "Light (300)" },
  { value: "400", label: "Normal (400)" },
  { value: "500", label: "Medium (500)" },
  { value: "600", label: "Semi Bold (600)" },
  { value: "700", label: "Bold (700)" },
  { value: "800", label: "Extra Bold (800)" },
  { value: "900", label: "Black (900)" },
];

const alignmentOptions = [
  { value: "", label: "None" },
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
  { value: "justify", label: "Justify" },
];

const breakpoints = [
  { key: "xs" as const, label: "Mobile (xs)", description: "Base / < 1080px", icon: Smartphone },
  { key: "sm" as const, label: "Tablet (sm)", description: "≥ 1080px", icon: Tablet },
  { key: "md" as const, label: "Desktop (md)", description: "≥ 1230px", icon: Laptop },
  { key: "lg" as const, label: "Large (lg)", description: "≥ 1430px", icon: Monitor },
  { key: "xl" as const, label: "Extra Large (xl)", description: "≥ 1750px", icon: Monitor },
];

export const ResponsiveStylesModal: React.FC<ResponsiveStylesModalProps> = ({
  isOpen,
  responsiveStyles,
  onStylesChange,
  onApply,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"all" | "fontSize" | "fontWeight" | "textAlign">("all");

  if (!isOpen) return null;

  const handleStyleChange = (
    breakpoint: keyof UnifiedResponsiveStyles,
    property: keyof BreakpointStyle,
    value: string
  ) => {
    onStylesChange({
      ...responsiveStyles,
      [breakpoint]: {
        ...responsiveStyles[breakpoint],
        [property]: value,
      },
    });
  };

  const tabs = [
    { key: "all" as const, label: "All", icon: null },
    { key: "fontSize" as const, label: "Font Size", icon: Type },
    { key: "fontWeight" as const, label: "Font Weight", icon: Bold },
    { key: "textAlign" as const, label: "Alignment", icon: AlignLeft },
  ];

  const renderSelect = (
    breakpoint: keyof UnifiedResponsiveStyles,
    property: keyof BreakpointStyle,
    options: { value: string; label: string }[],
    label: string
  ) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-500">{label}</label>
      <select
        value={responsiveStyles[breakpoint][property]}
        onChange={(e) => handleStyleChange(breakpoint, property, e.target.value)}
        className="border border-gray-300 px-2 py-1.5 rounded text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Responsive Text Styles</h3>
          <p className="text-sm text-gray-500 mt-1">
            Set font size, weight, and alignment for each breakpoint
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-white px-4">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === key
                  ? "border-primary-main text-primary-main"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {breakpoints.map(({ key, label, description, icon: Icon }) => (
              <div
                key={key}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50/50"
              >
                {/* Breakpoint Header */}
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                  <Icon className="w-5 h-5 text-gray-600" />
                  <div>
                    <span className="font-medium text-gray-900">{label}</span>
                    <span className="text-xs text-gray-500 ml-2">{description}</span>
                  </div>
                </div>

                {/* Style Controls */}
                <div className="grid grid-cols-3 gap-4">
                  {(activeTab === "all" || activeTab === "fontSize") &&
                    renderSelect(key, "fontSize", fontSizeOptions, "Font Size")}
                  {(activeTab === "all" || activeTab === "fontWeight") &&
                    renderSelect(key, "fontWeight", fontWeightOptions, "Font Weight")}
                  {(activeTab === "all" || activeTab === "textAlign") &&
                    renderSelect(key, "textAlign", alignmentOptions, "Text Align")}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 justify-end p-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-100 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onApply}
            className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-all"
          >
            Apply Styles
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper to create empty styles
export const createEmptyResponsiveStyles = (): UnifiedResponsiveStyles => ({
  xs: { fontSize: "", fontWeight: "", textAlign: "" },
  sm: { fontSize: "", fontWeight: "", textAlign: "" },
  md: { fontSize: "", fontWeight: "", textAlign: "" },
  lg: { fontSize: "", fontWeight: "", textAlign: "" },
  xl: { fontSize: "", fontWeight: "", textAlign: "" },
});
